import React, { useEffect } from "react";
import {
  FaCheckCircle,
  FaExclamationTriangle,
  FaInfoCircle,
  FaSpinner,
  FaExclamationCircle,
  FaTimes,
} from "react-icons/fa";

export interface BannerProps {
  id: string;
  text: string;
  type: "info" | "warning" | "error" | "success" | "loading";
  title?: string;
  timeout?: number;
  closeButton?: boolean;
  onClose?: () => void;
}

const bannerStyles = {
  success: {
    background: "bg-gradient-to-r from-[#f8fcf9] to-[#ffffff]",
    icon: FaCheckCircle,
    iconColor: "text-[#15803d]",
  },
  error: {
    background: "bg-gradient-to-r from-[#fef2f2] to-[#ffffff]",
    icon: FaExclamationTriangle,
    iconColor: "text-[#b91c1c]",
  },
  info: {
    background: "bg-gradient-to-r from-[#f3f8ff] to-[#ffffff]",
    icon: FaInfoCircle,
    iconColor: "text-[#1d4ed8]",
  },
  loading: {
    background: "bg-gradient-to-r from-[#f3f8ff] to-[#ffffff]",
    icon: FaSpinner,
    iconColor: "text-[#1d4ed8]",
  },
  warning: {
    background: "bg-gradient-to-r from-[#fffbeb] to-[#ffffff]",
    icon: FaExclamationCircle,
    iconColor: "text-[#d97706]",
  },
};

export const Banner: React.FC<BannerProps> = ({
  text,
  type,
  title,
  timeout = 0,
  closeButton = true,
  onClose,
}) => {
  const style = bannerStyles[type];
  const IconComponent = style.icon;

  useEffect(() => {
    if (timeout > 0) {
      const timer = setTimeout(() => {
        onClose?.();
      }, timeout);
      return () => clearTimeout(timer);
    }
  }, [timeout, onClose]);

  return (
    <div
      className={`
        flex items-start gap-3 p-4 rounded-sm
        ${style.background}
        border-[2px] border-white
        shadow-lg relative
        w-[400px] animate-fade-in
      `}
    >
      <div className={`flex-shrink-0 ${style.iconColor}`}>
        <div className="p-2 rounded-full bg-white shadow-sm">
          <IconComponent
            className={`text-lg ${type === "loading" ? "animate-spin" : ""}`}
          />
        </div>
      </div>

      <div className="flex-1 min-w-0">
        {title && (
          <h4 className="font-semibold text-gray-900 text-sm mb-1">{title}</h4>
        )}
        <p className="text-sm text-gray-600">{text}</p>
      </div>

      {closeButton && (
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 
            transition-colors duration-150 hover:bg-black/5 rounded-full p-1 
            focus:ring-transparent"
        >
          <FaTimes />
        </button>
      )}
    </div>
  );
};
